/**
 * The 8 onboarding step components + StepNav + StepRenderer.
 * Each step receives { state, set, next, back, isFirst, isLast }.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  type OnboardingState,
  NICHES, VOICES, TOPIC_EXAMPLES, TONES,
  ChoicesSummary, VoiceWave, Confetti,
} from "./shared.tsx";

type SetFn = (key: keyof OnboardingState, value: any) => void;

interface StepProps {
  state: OnboardingState;
  set: SetFn;
  next: () => void;
  back: (() => void) | null;
  isFirst: boolean;
  isLast: boolean;
}

// ── 1. WELCOME ──────────────────────────────────────────────────────────────
export function WelcomeStep({ next }: StepProps) {
  return (
    <div className="anim-in" style={{ display: "flex", flexDirection: "column", gap: 24, maxWidth: 460 }}>
      <div className="label">Welcome</div>
      <div>
        <h1 style={{ fontSize: 36, lineHeight: 1.1 }}>You're three minutes from your first video.</h1>
        <p className="muted" style={{ marginTop: 12, fontSize: 16, lineHeight: 1.55 }}>
          We'll set up your channel, pick a voice, and queue your first script. You can change any of it later — nothing's locked in.
        </p>
      </div>
      <ol style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 10 }}>
        {([
          ["Tell us what your first video's about", "60 seconds"],
          ["Pick a niche, format, and voice", "90 seconds"],
          ["Connect YouTube (or skip for now)", "30 seconds"],
          ["Review your AI-drafted script", "your call"],
        ] as const).map(([t, sub], i) => (
          <li key={t} style={{ display: "flex", gap: 12, alignItems: "flex-start", fontSize: 14 }}>
            <span className="mono" style={{
              width: 22, height: 22, borderRadius: "50%",
              background: "var(--accent-soft)", color: "var(--accent)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 11, fontWeight: 600, flexShrink: 0,
            }}>{i + 1}</span>
            <div>
              <div style={{ color: "var(--fg)" }}>{t}</div>
              <div className="muted" style={{ fontSize: 12, marginTop: 1 }}>{sub}</div>
            </div>
          </li>
        ))}
      </ol>
      <div><button className="btn lg" onClick={next}>Get started →</button></div>
    </div>
  );
}

// ── 2. TOPIC ────────────────────────────────────────────────────────────────
export function TopicStep({ state, set, next, back }: StepProps) {
  const can = state.topic.trim().length >= 4;
  return (
    <div className="anim-in" style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 540 }}>
      <div className="label">Step · First video</div>
      <div>
        <h1>What's your first video about?</h1>
        <p className="muted" style={{ marginTop: 8, fontSize: 15 }}>One sentence. We'll turn it into a script you can edit in the next step.</p>
      </div>
      <textarea className="input" placeholder="e.g. The Soviet space dog program, explained from launch to today" value={state.topic} onChange={e => set("topic", e.target.value)} rows={3} autoFocus />
      <div>
        <div className="label" style={{ marginBottom: 10 }}>Or try one of these</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {TOPIC_EXAMPLES.map(t => (
            <button key={t} className="chip" onClick={() => set("topic", t)}>{t}</button>
          ))}
        </div>
      </div>
      <StepNav back={back} next={next} canNext={can} />
    </div>
  );
}

// ── 3. FORMAT ───────────────────────────────────────────────────────────────
export function FormatStep({ state, set, next, back }: StepProps) {
  return (
    <div className="anim-in" style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 580 }}>
      <div className="label">Step · Format</div>
      <div>
        <h1>How should we shoot it?</h1>
        <p className="muted" style={{ marginTop: 8, fontSize: 15 }}>You can mix formats later. This is just for your first video.</p>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        {([
          { id: "long", label: "Long-form", ratio: "16:9", time: "10–30 min", body: "Standard YouTube videos. Best for explainers, stories, and ad revenue." },
          { id: "short", label: "Shorts", ratio: "9:16", time: "Up to 60s", body: "Vertical. Best for hooks, reach, and rapid testing." },
        ] as const).map(f => (
          <button key={f.id} className={"option" + (state.format === f.id ? " selected" : "")} onClick={() => set("format", f.id)}>
            <FormatPreview ratio={f.ratio} />
            <div style={{ marginTop: 12, fontWeight: 600, fontSize: 16 }}>{f.label}</div>
            <div className="label" style={{ marginTop: 4 }}>{f.ratio} · {f.time}</div>
            <div className="muted" style={{ marginTop: 8, fontSize: 13, lineHeight: 1.5 }}>{f.body}</div>
          </button>
        ))}
      </div>
      <StepNav back={back} next={next} canNext={!!state.format} />
    </div>
  );
}

function FormatPreview({ ratio }: { ratio: string }) {
  const isLong = ratio === "16:9";
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 70, background: "var(--surface)", borderRadius: 8 }}>
      <div style={{
        width: isLong ? 96 : 36, height: isLong ? 54 : 64,
        background: "var(--bg)", border: "1px solid var(--rule-2)", borderRadius: 4,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <svg width="14" height="14" viewBox="0 0 14 14"><polygon points="3,2 3,12 12,7" fill="var(--accent)" /></svg>
      </div>
    </div>
  );
}

// ── 4. NICHE ────────────────────────────────────────────────────────────────
export function NicheStep({ state, set, next, back }: StepProps) {
  return (
    <div className="anim-in" style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 720 }}>
      <div className="label">Step · Niche</div>
      <div>
        <h1>Pick a niche preset.</h1>
        <p className="muted" style={{ marginTop: 8, fontSize: 15 }}>This pre-configures your script style and image generation. Change it later from settings.</p>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
        {NICHES.map(n => (
          <button key={n.id} className={"option" + (state.niche === n.id ? " selected" : "")} onClick={() => set("niche", n.id)} style={{ padding: 14 }}>
            <NicheGlyph id={n.id} />
            <div style={{ marginTop: 10, fontWeight: 600, fontSize: 14 }}>{n.name}</div>
            <div className="muted" style={{ marginTop: 4, fontSize: 12, lineHeight: 1.4 }}>{n.blurb}</div>
          </button>
        ))}
      </div>
      <StepNav back={back} next={next} canNext={!!state.niche} />
    </div>
  );
}

function NicheGlyph({ id }: { id: string }) {
  const common = { width: 32, height: 32, viewBox: "0 0 32 32" };
  const stroke = { stroke: "var(--accent)", strokeWidth: 1.6, fill: "none", strokeLinecap: "round" as const, strokeLinejoin: "round" as const };
  const glyphs: Record<string, React.ReactNode> = {
    reddit:  <svg {...common}><circle cx="16" cy="17" r="9" {...stroke} /><circle cx="16" cy="6" r="2" {...stroke} /><path d="M16 8v6" {...stroke} /></svg>,
    top10:   <svg {...common}><path d="M6 8h20M6 14h14M6 20h20M6 26h10" {...stroke} /></svg>,
    history: <svg {...common}><circle cx="16" cy="16" r="11" {...stroke} /><path d="M16 8v8l5 3" {...stroke} /></svg>,
    motiv:   <svg {...common}><path d="M8 20l4-12 4 8 4-4 4 8" {...stroke} /></svg>,
    crime:   <svg {...common}><circle cx="14" cy="14" r="6" {...stroke} /><path d="M18 18l6 6" {...stroke} /></svg>,
    facts:   <svg {...common}><path d="M16 6v4M16 22v4M6 16h4M22 16h4M9 9l3 3M20 20l3 3M9 23l3-3M20 12l3-3" {...stroke} /></svg>,
  };
  return <div style={{ width: 32, height: 32 }}>{glyphs[id] || <svg {...common}><rect x="6" y="6" width="20" height="20" rx="3" {...stroke} /></svg>}</div>;
}

// ── 5. CHANNEL + TONE ───────────────────────────────────────────────────────
export function ChannelStep({ state, set, next, back }: StepProps) {
  const can = state.channelName.trim().length >= 2;
  return (
    <div className="anim-in" style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 540 }}>
      <div className="label">Step · Your channel</div>
      <div>
        <h1>What's your channel called?</h1>
        <p className="muted" style={{ marginTop: 8, fontSize: 15 }}>Used for thumbnails, captions, and the channel signature in your scripts.</p>
      </div>
      <div>
        <label className="label" style={{ marginBottom: 6, display: "block" }}>Channel name</label>
        <input className="input" placeholder="e.g. Deep Tank, Reddit Night, History/HQ" value={state.channelName} onChange={e => set("channelName", e.target.value)} autoFocus />
      </div>
      <div>
        <label className="label" style={{ marginBottom: 8, display: "block" }}>Tone of voice</label>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
          {TONES.map(t => (
            <button key={t.id} className={"option" + (state.tone === t.id ? " selected" : "")} onClick={() => set("tone", t.id)} style={{ padding: 12 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{t.label}</div>
              <div className="muted" style={{ marginTop: 4, fontSize: 12, lineHeight: 1.4 }}>{t.blurb}</div>
            </button>
          ))}
        </div>
      </div>
      <StepNav back={back} next={next} canNext={can} />
    </div>
  );
}

// ── 6. VOICE ────────────────────────────────────────────────────────────────
export function VoiceStep({ state, set, next, back }: StepProps) {
  const [playing, setPlaying] = useState<string | null>(null);
  const togglePlay = (id: string) => setPlaying(p => p === id ? null : id);
  return (
    <div className="anim-in" style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 720 }}>
      <div className="label">Step · Narrator</div>
      <div>
        <h1>Pick a voice.</h1>
        <p className="muted" style={{ marginTop: 8, fontSize: 15 }}>Tap to preview. Every voice can read every script — pick whatever fits the vibe.</p>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
        {VOICES.map((v, i) => {
          const isSel = state.voice === v.id;
          const isPlaying = playing === v.id;
          return (
            <button key={v.id} className={"option" + (isSel ? " selected" : "")} onClick={() => set("voice", v.id)} style={{ display: "flex", alignItems: "center", gap: 14, padding: 14 }}>
              <span
                onClick={e => { e.stopPropagation(); togglePlay(v.id); }}
                style={{
                  width: 36, height: 36, borderRadius: "50%",
                  background: isPlaying ? "var(--accent)" : "var(--surface)",
                  color: isPlaying ? "#fff" : "var(--fg)",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                  border: "1px solid var(--rule)",
                }}
              >
                {isPlaying ? (
                  <svg width="10" height="12" viewBox="0 0 10 12"><rect x="1" y="1" width="2.5" height="10" /><rect x="6.5" y="1" width="2.5" height="10" /></svg>
                ) : (
                  <svg width="10" height="12" viewBox="0 0 10 12"><polygon points="2,1 2,11 9,6" fill="currentColor" /></svg>
                )}
              </span>
              <div style={{ flex: 1, textAlign: "left", minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{v.name}</div>
                <div className="muted" style={{ fontSize: 12, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{v.accent} · {v.style}</div>
              </div>
              <span style={{ color: "var(--fg-muted)", flexShrink: 0 }}>
                <VoiceWave seed={i + 1} playing={isPlaying} />
              </span>
            </button>
          );
        })}
      </div>
      <StepNav back={back} next={next} canNext={!!state.voice} />
    </div>
  );
}

// ── 7. YOUTUBE ───────────────────────────────────────────────────────────────
export function YouTubeStep({ state, set, next, back }: StepProps) {
  return (
    <div className="anim-in" style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 500 }}>
      <div className="label">Step · Publishing</div>
      <div>
        <h1>Publish straight to YouTube?</h1>
        <p className="muted" style={{ marginTop: 8, fontSize: 15 }}>Optional. If you connect, we can publish directly. You can also just download the MP4 and upload manually.</p>
      </div>
      <div className="card" style={{ padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 10,
            background: state.youtubeConnected ? "var(--accent-soft)" : "var(--surface)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill={state.youtubeConnected ? "var(--accent)" : "var(--fg-muted)"}>
              <path d="M21.6 7.2a2.5 2.5 0 00-1.76-1.76C18.25 5 12 5 12 5s-6.25 0-7.84.44A2.5 2.5 0 002.4 7.2C2 8.79 2 12 2 12s0 3.21.4 4.8a2.5 2.5 0 001.76 1.76C5.75 19 12 19 12 19s6.25 0 7.84-.44a2.5 2.5 0 001.76-1.76C22 15.21 22 12 22 12s0-3.21-.4-4.8zM10 15V9l5 3-5 3z" />
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 15 }}>YouTube account</div>
            <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>
              {state.youtubeConnected ? "Connected as @yourchannel" : "Not connected"}
            </div>
          </div>
          {state.youtubeConnected
            ? <span className="label" style={{ color: "var(--success)" }}>● Linked</span>
            : <button className="btn" onClick={() => set("youtubeConnected", true)}>Connect</button>}
        </div>
        <hr className="hr" />
        <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 8, fontSize: 13 }}>
          {["Publish or schedule directly from the dashboard", "Auto-upload thumbnails and captions", "Read-only — we won't touch existing videos"].map(t => (
            <li key={t} style={{ display: "flex", gap: 8, color: "var(--fg-muted)" }}>
              <svg width="14" height="14" viewBox="0 0 14 14" style={{ marginTop: 3, flexShrink: 0 }}><path d="M3 7l3 3 5-6" stroke="var(--accent)" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>
              {t}
            </li>
          ))}
        </ul>
      </div>
      <StepNav back={back} next={next} canNext={true} nextLabel={state.youtubeConnected ? "Continue" : "Skip for now"} />
    </div>
  );
}

// ── 8. KICKOFF ───────────────────────────────────────────────────────────────
export function KickoffStep({ state, back }: StepProps) {
  const navigate = useNavigate();
  // (session 10, A5; owner 2026-10-08) no simulated drafting phase: nothing is generated on this screen.
  // The real video is created on the next one (NewVideoPage).

  const handleOpenScript = () => {
    // Carry the onboarding topic into the real video-creation flow (NewVideoPage
    // reads ?topic= to prefill guidance and ?preset= to preselect a preset). The
    // niche id is passed as a preset hint; NewVideoPage falls back to its preset
    // picker if it doesn't match a real preset, so this is always safe.
    const params = new URLSearchParams();
    if (state.topic?.trim()) params.set("topic", state.topic.trim());
    if (state.niche) params.set("preset", state.niche);
    const qs = params.toString();
    navigate(`/videos/new${qs ? `?${qs}` : ""}`);
  };

  return (
    <div className="anim-in" style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 540, position: "relative" }}>
      <Confetti />
      <div className="label" style={{ color: "var(--accent)" }}>● Setup complete</div>
      <div>
        <h1>You're set up.</h1>
        <p className="muted" style={{ marginTop: 8, fontSize: 15 }}>
          Next, create your first video. You'll review every word before anything renders.
        </p>
      </div>
      <div className="card" style={{ padding: 18 }}>
        <div className="label" style={{ marginBottom: 12 }}>Your setup</div>
        <ChoicesSummary state={state} />
      </div>
      <div style={{ display: "flex", gap: 10 }}>
        <button className="btn lg" onClick={handleOpenScript}>Create my first video →</button>
        <button className="btn ghost" onClick={back ?? undefined}>Back</button>
      </div>
    </div>
  );
}

// ── Shared nav ──────────────────────────────────────────────────────────────
export function StepNav({ back, next, canNext, nextLabel = "Continue", showBack = true }: {
  back: (() => void) | null;
  next: () => void;
  canNext: boolean;
  nextLabel?: string;
  showBack?: boolean;
}) {
  return (
    <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
      <button className="btn" onClick={next} disabled={!canNext}>{nextLabel} →</button>
      {showBack && back && <button className="btn ghost" onClick={back}>Back</button>}
    </div>
  );
}

// ── Step renderer dispatch ──────────────────────────────────────────────────
const STEP_MAP: Record<string, React.FC<StepProps>> = {
  welcome: WelcomeStep,
  topic: TopicStep,
  format: FormatStep,
  niche: NicheStep,
  channel: ChannelStep,
  voice: VoiceStep,
  youtube: YouTubeStep,
  kickoff: KickoffStep,
};

export function StepRenderer({ stepId, ...props }: StepProps & { stepId: string }) {
  const C = STEP_MAP[stepId];
  return C ? <C {...props} /> : <div>Unknown step: {stepId}</div>;
}
